import React, { useEffect, useRef, useState } from 'react';
import { KEY_CODES } from 'utility/constants';
import ErrorBoundaryWithLogger from 'common/ErrorBoundary/ErrorBoundary';
import { useAppSelector } from 'store/store';
import {
  getFeatureFlagByNameSelector,
  headerTaxoCategoriesSelector,
  progressiveHeaderTaxoCategoriesSelector,
  headerClientLoadingSelector,
  progressiveHeaderClientLoadingSelector,
} from 'store/selectors';

import MenuContainer from './MenuContainer';
import ProgressiveMenuContainer from './ProgressiveMenuContainer';
import './DesktopNav.scss';
import PrimaryCategories from './PrimaryCategories';
import PrimaryCategoriesNoContent from './PrimaryCategoriesNoContent';
import PrimaryCategoriesPending from './PrimaryCategoriesPending';

export default function DesktopNav({
  viewTopNavAnalyticsEvent,
}: Readonly<{
  viewTopNavAnalyticsEvent: () => void;
}>) {
  const progressiveHeaderDesktopEnabled = useAppSelector(
    getFeatureFlagByNameSelector('progressiveTaxoDesktop'),
  );
  const headerDesktopNavData = useAppSelector(headerTaxoCategoriesSelector);
  const progressiveDesktopNavData = useAppSelector(progressiveHeaderTaxoCategoriesSelector);
  const desktopNavData = progressiveHeaderDesktopEnabled
    ? progressiveDesktopNavData
    : headerDesktopNavData;

  const headerIsLoading = useAppSelector(headerClientLoadingSelector);
  const progressiveIsLoading = useAppSelector(progressiveHeaderClientLoadingSelector);
  const isLoading = progressiveHeaderDesktopEnabled ? progressiveIsLoading : headerIsLoading;

  // Get taxonomy data and filter out any instances of unset Homr data
  const [showSubMenu, setShowSubMenu] = useState(false);
  const firstSubLinkRef = useRef<HTMLAnchorElement | null>(null);
  const [currentCategoryIndex, setCurrentCategoryIndex] = useState<number | null>(0);
  const [keyBoardNavigation, setkeyBoardNavigation] = useState<boolean>(false);

  useEffect(() => {
    if (document?.body?.classList?.contains('navigation-open')) {
      document.body.classList.remove('navigation-open');
    }
  }, []);

  useEffect(() => {
    if (keyBoardNavigation === true) {
      // focus the first link in the menu if keyboard navigation was used
      firstSubLinkRef.current?.focus();
    }
  });

  if (!desktopNavData && !isLoading) {
    throw new Error(
      'Homr did not return any Taxonomy2 content. Make sure the correct data is being returned!',
    );
  }

  const closeMenu = (): void => {
    setShowSubMenu(false);
  };

  const openMenu = (index: number): void => {
    if (!desktopNavData) {
      return;
    }
    const navigationCategory = desktopNavData[index].title;
    viewTopNavAnalyticsEvent({ NavigationCategory: navigationCategory });

    if (!showSubMenu) {
      setShowSubMenu(true);
    }
    setCurrentCategoryIndex(index);
  };

  const changeFocus = (offset = 0): void => {
    if (currentCategoryIndex !== null) {
      document.getElementById(`desktop-nav-l0-${offset}`)?.focus();
    }
  };

  const handleMouseClick = (
    event: React.MouseEvent<HTMLAnchorElement, MouseEvent>,
    index: number,
  ): void => {
    if (currentCategoryIndex === index && showSubMenu) {
      return;
    }
    event.preventDefault();
    setkeyBoardNavigation(false);
    if (desktopNavData[index].csvData.length > 0) {
      openMenu(index);
    } else {
      window.location.href = desktopNavData[index].link;
    }
  };

  const handleKeyDownPrimaryCategory = (event: React.KeyboardEvent, index: number) => {
    if (event.key === KEY_CODES.SPACE) {
      // prevents the spacebar from scrolling
      event.preventDefault();
      setkeyBoardNavigation(true);
      openMenu(index);
    }
    if (event.key === KEY_CODES.ESCAPE) {
      closeMenu();
    }
  };

  return (
    <div className="desktop-nav-container">
      <div className="desktop-nav-border-container">
        <nav
          className={`desktop-nav-container__nav hmf-subheader-m hmf-py-xxs ${
            currentCategoryIndex === null ? '' : 'active'
          }`}>
          {!desktopNavData && isLoading && <PrimaryCategoriesPending />}
          {desktopNavData?.length > 0 ? (
            <PrimaryCategories
              desktopNavData={desktopNavData}
              showSubMenu={showSubMenu}
              currentCategoryIndex={currentCategoryIndex}
              handleMouseClick={handleMouseClick}
              handleKeyDownPrimaryCategory={handleKeyDownPrimaryCategory}
            />
          ) : (
            <PrimaryCategoriesNoContent />
          )}
        </nav>
      </div>
      {progressiveHeaderDesktopEnabled ? (
        // Updated Desktop Nav content that places l1 categories dynamically with flexbox.
        // This should become the default experience once we switch over to the progressive-
        // taxo entirely
        //
        // - Blair Wilcox 6/4/2025
        <ErrorBoundaryWithLogger componentId="DesktopNav-ProgressiveMenuContainer">
          <ProgressiveMenuContainer
            currentCategoryIndex={currentCategoryIndex as number}
            showSubMenu={showSubMenu}
            closeMenu={closeMenu}
            changeFocus={changeFocus}
            firstSubLinkRef={firstSubLinkRef}
          />
        </ErrorBoundaryWithLogger>
      ) : (
        // Original experience with headerTaxo data that uses pre-defined columns.
        // Once we've migrated over to progressive-taxo entirely we can remove this
        //
        // - Blair Wilcox 6/4/2025
        <ErrorBoundaryWithLogger
          componentId="DesktopNav-MenuContainer"
          fallback={<PrimaryCategoriesNoContent />}>
          <MenuContainer
            desktopNavData={desktopNavData}
            index={currentCategoryIndex as number}
            showSubMenu={showSubMenu}
            closeMenu={closeMenu}
            changeFocus={changeFocus}
            firstSubLinkRef={firstSubLinkRef}
          />
        </ErrorBoundaryWithLogger>
      )}
    </div>
  );
}
